'use client';

/**
 * AssistantLayout — top-level chrome shell for the AI assistant v3.
 *
 * v3 design turn 6 replaced the old video-background + glass-card overlay
 * with the design's own full-page frame: a warm cream (#FFFAF5) canvas,
 * split into [sidebar | main column] by the page itself — this component
 * only supplies the outer `h-dvh` flex row and the CommonNinja bottom-bar
 * inset reservation. No video, no dark scrim, no expanding card, no
 * landing↔expanded morph — UIState.LANDING now renders inside this SAME
 * frame (see page.tsx's unified empty state).
 *
 * Note: live-agent handoff still bridges to a human rep natively over our
 * own WebSocket, rendered inline via UIState.HANDOFF (message-block.tsx's
 * human-agent bubble + presence-ribbon.tsx) — no inline container needed
 * here.
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import { useBottomBarInset } from '@/hooks/useBottomBarInset';

interface AssistantLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export const AssistantLayout = memo(function AssistantLayout({
  children,
  className,
}: AssistantLayoutProps) {
  // #82: reserve space for the CommonNinja HSA/FSA sticky bar so it never
  // overlaps the prompt input. Applied HERE (the h-dvh flex row) — shrinking
  // the container's usable height lifts the composer no matter which
  // uiState's content is currently mounted in the main column. Auto-measured
  // (bar wraps taller on mobile) and collapses to 0 when dismissed/absent.
  const bottomBarInset = useBottomBarInset();

  return (
    <div
      className={cn(
        // Work Sans is the v3 design's BODY font — set it here so every
        // descendant inherits it instead of the site's global font stack
        // (per-label font-['Work_Sans'] classes remain harmless no-ops).
        "flex h-dvh w-full flex-row bg-[#FFFAF5] font-['Work_Sans'] antialiased",
        className
      )}
      style={bottomBarInset > 0 ? { paddingBottom: bottomBarInset } : undefined}
    >
      {children}
    </div>
  );
});
